import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Theme } from '../types';
import { getStorageItem, setStorageItem } from '../utils/storage';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const lightTheme: Theme = {
  bg: 'bg-stone-100',
  card: 'bg-[#f7f1e8]',
  text: 'text-stone-950',
  textSecondary: 'text-stone-600',
  border: 'border-stone-300/80',
  input: 'bg-white/90 text-stone-950 border-stone-300 placeholder:text-stone-400',
  hover: 'hover:bg-white/75',
};

const darkTheme: Theme = {
  bg: 'bg-[#12100d]',
  card: 'bg-[#1c1915]',
  text: 'text-stone-100',
  textSecondary: 'text-stone-400',
  border: 'border-stone-800',
  input: 'bg-[#24201b] text-stone-100 border-stone-700 placeholder:text-stone-500',
  hover: 'hover:bg-[#2a251f]',
};

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStorageItem('gym-darkmode').then(saved => {
      if (saved) setDarkMode(JSON.parse(saved));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (loading) return;
    const bgColor = darkMode ? '#12100d' : '#f2ece2';
    document.documentElement.style.setProperty('--app-bg', bgColor);
    document.documentElement.style.setProperty('--app-surface', darkMode ? '#1c1915' : '#f7f1e8');
    document.documentElement.style.setProperty('--app-accent', darkMode ? '#d97745' : '#b4532a');
    document.documentElement.style.setProperty('--app-accent-soft', darkMode ? '#2f2218' : '#efd3c3');
  }, [darkMode, loading]);

  const toggleDarkMode = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    await setStorageItem('gym-darkmode', JSON.stringify(newValue));
  };

  const theme = darkMode ? darkTheme : lightTheme;

  // Prevent flash of wrong theme while loading
  if (loading) {
    return null;
  }

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, theme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
